/* eslint-disable camelcase */
import { type UseMutationResult, useMutation } from 'react-query';

import {
  CustomReportsService,
  Wealthlane_ReportService_Dtos_CustomReport_CustomReportSaveAndRunResponseDto,
} from '@wealthlane/api-services/wealthlane-report-service';
import { toast } from 'react-toastify';
import ToastComponent from '../../components/toast';
import _ from 'lodash';

const mapUserFieldFormats = (item: any) => ({
  ...item,
  userFieldFormats: item?.userFieldFormats?.map((format: any) => ({
    key: format.key,
    value: !_.isEmpty(format.value) ? format.value : format.placeholder,
    placeholder: format.placeholder,
  })),
});

type BucketTag = 'required' | 'optional' | 'udf';

const normalizeSequenceNumbersGlobally = (payload: Record<string, any>) => {
  const required = (payload.requiredFieldBuckets ?? []).map((f: any) => ({ ...f, _bucket: 'required' as BucketTag }));
  const optional = (payload.optionalFieldBuckets ?? []).map((f: any) => ({ ...f, _bucket: 'optional' as BucketTag }));
  const udf = (payload.udfFieldBuckets ?? []).map((f: any) => ({ ...f, _bucket: 'udf' as BucketTag }));
  const allFields = [...required, ...optional, ...udf];

  const selected = allFields
    .filter((f) => f.isSelected !== false)
    .sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0))
    .map((f, i) => ({ ...f, sequenceNumber: i + 1 }));

  const unselected = allFields.filter((f) => f.isSelected === false);
  const all = [...selected, ...unselected];

  const splitBucket = (tag: BucketTag) =>
    all.filter((f) => f._bucket === tag).map(({ _bucket, ...rest }) => rest);

  return {
    requiredFieldBuckets: splitBucket('required'),
    optionalFieldBuckets: splitBucket('optional'),
    udfFieldBuckets: splitBucket('udf'),
  };
};

const transformPayload = (payload: Record<string, any>) => {
  const withFormats = {
    ...payload,
    requiredFieldBuckets: payload.requiredFieldBuckets?.map(mapUserFieldFormats),
    optionalFieldBuckets: payload.optionalFieldBuckets?.map(mapUserFieldFormats),
  };
  const normalized = normalizeSequenceNumbersGlobally(withFormats);
  return { ...withFormats, ...normalized };
};

const usePostSubmitAndRunDynamicReport = (
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>,
  openSubmitModal: () => void,
  setTaskReferanceNo?: any
): UseMutationResult<
  Wealthlane_ReportService_Dtos_CustomReport_CustomReportSaveAndRunResponseDto | undefined,
  unknown,
  any,
  unknown
> => {
  return useMutation(
    async (payload: any) => {
      let newPayload = { ...payload, reportConfigurationId: payload.id };
      newPayload = transformPayload(newPayload);
      return await handleRequest(
        CustomReportsService.postApiReportApiAppCustomReportsSubmitAndRunReport(newPayload)
      );
    },
    {
      onSuccess: async (
        response:
          | Wealthlane_ReportService_Dtos_CustomReport_CustomReportSaveAndRunResponseDto
          | undefined
      ) => {
        if (response && response?.isSuccess === true) {
          openSubmitModal();
          setTaskReferanceNo(response.taskRefrenceNo);
        } else if (response && response?.error) {
          toast.error(<ToastComponent label='Error' message={`${response?.error[0]}`} />);
        }
      },
      onError: (err) => {
        console.log('error', err);
      },
    }
  );
};

export default usePostSubmitAndRunDynamicReport;
